/**
 *
 *  TruBe Tooltip
 *  UI-Kit Component
 *
 *  Created by Vladimir Shishlyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types"; // use directly
import { findDOMNode } from "react-dom";

import { makeCancelable } from "../../utils";

const ARROW_OFFSET = 4;
const ARROW_WIDTH = 20;
const MAX_WIDTH = 256;

class Tooltip extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    tooltipInnerStyle: PropTypes.object,
    element: PropTypes.object,
    isShow: PropTypes.bool,
    color: PropTypes.string,
    adjust: PropTypes.bool,
    autoHide: PropTypes.bool,
    delay: PropTypes.number,
    inside: PropTypes.bool,
    placement: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
      .isRequired,
    duration: PropTypes.number,
    onAfterHide: PropTypes.func,
    onClick: PropTypes.func,
  };

  static defaultProps = {
    autoHide: true,
    adjust: false,
    duration: 300,
  };

  constructor(props) {
    super(props);

    this.state = {
      tooltipPosition: {
        left: null,
        top: null,
        right: null,
        bottom: null,
      },
      arrowOffset: {
        left: null,
        right: null,
      },
    };
  }

  getPosition(inside) {
    var top, left, width, height;

    if (this.props.element) {
      let elem = findDOMNode(this.props.element);
      let rect = elem.getBoundingClientRect();

      top =
        rect.top +
        window.pageYOffset -
        window.document.documentElement.clientTop;
      left =
        rect.left +
        window.pageXOffset -
        window.document.documentElement.clientLeft;
      width = rect.width;
      height = rect.height;
    }

    return Object.assign({}, inside ? { top: 0, left: "0" } : { top, left }, {
      width,
      height,
    });
  }

  show() {
    const { adjust, duration, inside } = this.props;
    var pos, actualWidth, actualHeight, placement, tooltipPosition, arrowOffset;

    if (this.props.children && !this.props.disabled) {
      if (this.hideWithAnimationCancelablePromise) {
        this.hideWithAnimationCancelablePromise.cancel();
        this.hideWithAnimationCancelablePromise = null;

        var leaveStatus = {
          leaveAnimation: false,
          leaveActiveAnimation: false,
        };
      }

      this.setState(
        Object.assign({ isShow: true, enterAnimation: true }, leaveStatus)
      );

      placement =
        typeof this.props.placement == "function"
          ? this.props.placement.call(this, this.props.element.current)
          : this.props.placement;

      pos = this.getPosition(inside);

      if (this.tooltipRef) {
        actualWidth =
          this.tooltipRef.offsetWidth > 256 || this.props.children.length > 50
            ? MAX_WIDTH
            : this.tooltipRef.offsetWidth;

        // Wait for the height to recalculate
        this.setState({ width: actualWidth + 1 /* Android fix */ }, () => {
          actualHeight = this.tooltipRef.offsetHeight;

          switch (placement) {
            case "bottom":
              tooltipPosition = {
                top: pos.top + pos.height,
                left: pos.left + pos.width / 2 - actualWidth / 2,
              };
              arrowOffset = {
                left: adjust ? actualWidth / 2 - ARROW_WIDTH / 2 : 0,
                right: "0",
              };
              break;
            case "top":
              tooltipPosition = {
                top: pos.top - actualHeight - ARROW_OFFSET,
                left: pos.left + pos.width / 2 - actualWidth / 2,
              };
              arrowOffset = {
                left: adjust ? actualWidth / 2 - ARROW_WIDTH / 2 : 0,
                right: "0",
                flipX: true /* Hack */,
              };
              break;
            case "left":
              tooltipPosition = {
                top: pos.top + pos.height / 2 - actualHeight / 2,
                left: pos.left - actualWidth,
              };
              arrowOffset = {
                left: adjust
                  ? Math.max(pos.width / 2, actualWidth - ARROW_WIDTH)
                  : 0,
                right: "0",
              };
              break;
            case "right":
              tooltipPosition = {
                top: pos.top + pos.height / 2 - actualHeight / 2,
                left: pos.left + pos.width,
              };
              arrowOffset = {
                left: adjust
                  ? Math.max(pos.width / 2, actualWidth - ARROW_WIDTH)
                  : 0,
                right: "0",
              };
              break;
            case "top left":
              tooltipPosition = {
                top: pos.top - actualHeight - ARROW_OFFSET,
                left: "0",
              };
              arrowOffset = {
                left: adjust
                  ? Math.min(
                      pos.width / 2 - ARROW_WIDTH - 8,
                      actualWidth - ARROW_WIDTH - 8
                    )
                  : 0,
                right: "0",
                flipX:
                  Math.min(pos.width / 2, actualWidth / 2 - ARROW_WIDTH - 8) <
                  actualWidth / 2,
              };
              break;
            case "top right":
              tooltipPosition = {
                top: pos.top - actualHeight - ARROW_OFFSET,
                left: pos.left + pos.width - actualWidth,
              };
              arrowOffset = {
                left: adjust
                  ? Math.min(
                      pos.width / 2 - ARROW_WIDTH - 8,
                      actualWidth - ARROW_WIDTH - 8
                    )
                  : 0,
                flipX:
                  true /* Hack */ ||
                  Math.min(pos.width / 2, actualWidth / 2 - ARROW_WIDTH - 8) >
                    actualWidth / 2,
              }; // TODO: fix this
              // arrowOffset = { right: adjust ? Math.min(pos.width / 2, actualWidth) : 0, flipX: Math.min(pos.width / 2, actualWidth / 2 - ARROW_WIDTH - 8) > actualWidth / 2 };
              break;
            default: // 'top'
              tooltipPosition = {
                top: pos.top - actualHeight - ARROW_OFFSET,
                left: pos.left + pos.width / 2 - actualWidth / 2,
              };
              arrowOffset = {
                left: adjust ? actualWidth / 2 - ARROW_WIDTH / 2 : 0,
                right: "0",
                flipX: true /* Hack */,
              };
          }

          var enterAnimationPromise = new Promise((resolve, reject) => {
            this.setState({
              tooltipPosition,
              arrowOffset,
              enterActiveAnimation: true,
            });

            setTimeout(() => {
              resolve();
            }, duration);
          });

          this.enterAnimationCancelablePromise = makeCancelable(
            enterAnimationPromise
          );

          this.enterAnimationCancelablePromise.promise.then(() => {
            this.setState({
              enterAnimation: false,
              enterActiveAnimation: false,
            });
          });
        });
      }
    }
  }

  hide() {
    const { duration } = this.props;

    if (this.enterAnimationCancelablePromise) {
      this.enterAnimationCancelablePromise.cancel();
      this.enterAnimationCancelablePromise = null;

      var enterStatus = {
        enterAnimation: false,
        enterActviveAnimation: false,
      };
    }

    var hideWithAnimationPromise = new Promise((resolve, reject) => {
      this.setState(
        Object.assign(
          {
            enterAnimation: false,
            enterActiveAnimation: false,
            leaveAnimation: true,
            leaveActiveAnimation: true,
          },
          enterStatus
        )
      );

      setTimeout(() => {
        resolve();
      }, duration);
    });

    this.hideWithAnimationCancelablePromise = makeCancelable(
      hideWithAnimationPromise
    );

    this.hideWithAnimationCancelablePromise.promise.then(() => {
      this.setState(
        {
          leaveAnimation: false,
          leaveActiveAnimation: false,
          isShow: false,
          width: "",
        },
        this.afterHide
      );
    });
  }

  afterHide() {
    this.hideWithAnimationCancelablePromise = null;

    this.props.onAfterHide && this.props.onAfterHide();
  }

  componentDidMount() {
    if (this.props.isShow) {
      this.show();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isShow && !prevProps.isShow) {
      this.show();
    } else if (
      !this.props.isShow &&
      prevProps.isShow &&
      !this.hideWithAnimationCancelablePromise
    ) {
      this.hide();
    }
  }

  componentWillUnmount() {
    this.tooltipRef = null;
    if (this.enterAnimationCancelablePromise) {
      this.enterAnimationCancelablePromise.cancel();
      this.enterAnimationCancelablePromise = null;
    }
    if (this.leaveAnimationCancelablePromise) {
      this.leaveAnimationCancelablePromise.cancel();
      this.leaveAnimationCancelablePromise = null;
    }
  }

  render() {
    return (
      <div
        ref={(elem) => (this.tooltipRef = elem)}
        className={`tbk-tooltip ${this.props.placement}${
          this.props.className ? ` ${this.props.className}` : ""
        }${this.state.isShow ? " is-show" : ""}${
          this.state.enterAnimation ? " enter" : ""
        }${this.state.enterActiveAnimation ? " enter-active" : ""}${
          this.state.leaveAnimation ? " leave" : ""
        }${this.state.leaveActiveAnimation ? " leave-active" : ""}`}
        role="tooltip"
        style={{
          ...this.props.style,
          visibility: this.state.isShow ? "visible" : "hidden",
          width: this.state.width,
          /*opacity: this.state.isShow ? 1 : 0, */
          top: this.state.tooltipPosition.top,
          left: this.state.tooltipPosition.left,
        }}
        onClick={this.props.onClick}
      >
        <div
          className={`tbk-tooltip-arrow${
            this.state.arrowOffset.flipX ? " flipX" : ""
          }`}
          style={{
            marginLeft: this.state.arrowOffset.left,
            marginRight: this.state.arrowOffset.right,
          }}
        />
        <div className="tbk-tooltip-inner">{this.props.children}</div>
      </div>
    );
  }
}

export default Tooltip;
